import { useCallback, useEffect, useRef, useState } from 'react'
import { Drawer } from 'vaul'

import { useViewportSize } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { useSupabaseClient } from '@supabase/auth-helpers-react'
import { Button, Loader, Tooltip } from '@mantine/core'
import { useBus, useListener } from 'react-bus'
import useMe from '@/hooks/useMe'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    Menu01,
    User01,
    SearchLg,
    Scan,
    MarkerPin01,
    Hash02,
} from '@untitled-ui/icons-react'
import onScan from 'onscan.js'

import useVersionCheck from '@/hooks/useVersionCheck'
import useNfcScanner from '@/hooks/useNfcScanner'
import BackofficeLayoutNavigation from './BackofficeLayoutNavigation'
import BalticLogo from '../icons/BalticLogo'
import CurrentLocation from '../shipments/CurrentLocation'
import SidebarLocationButton from './SidebarLocationButton'
import SidebarNotificationsButton from './SidebarNotificationsButton'
import CameraScannerButton from '../ui/CameraScannerButton'
import TypesenseGlobalSearch from '../ui/TypesenseGlobalSearch'
import NumberLookupSearch from '../ui/NumberLookupSearch'
import ScannedModals from '../ui/ScannedModals'
import { MOBILE_BREAKPOINT } from '@/lib/constants'

// Écran plein cadre pour les cas où le back-office ne peut pas s'afficher :
// profil illisible ou compte sans accès.
function GateScreen({ title, message, detail, action }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-5">
            <div className="flex flex-col items-center gap-3 text-center max-w-[360px]">
                <BalticLogo className="w-20 text-gray-900 mb-2" />
                <span className="font-medium text-lg">{title}</span>
                <span className="text-sm text-gray-500">{message}</span>
                {detail && (
                    <span className="text-xs text-gray-400 font-mono">
                        {detail}
                    </span>
                )}
                <div className="mt-2">{action}</div>
            </div>
        </div>
    )
}

export default function BackofficeLayout({ children }) {
    const location = useLocation()
    const navigate = useNavigate()
    const { name: userName, role, isLoading, error } = useMe()
    const { t } = useTranslation()
    const supabase = useSupabaseClient()
    const bus = useBus()

    useVersionCheck()

    const { width: viewportWidth } = useViewportSize()
    const isMobile = viewportWidth > 0 && viewportWidth <= MOBILE_BREAKPOINT

    const [drawerOpen, setDrawerOpen] = useState(false)

    // Close drawer on page change
    useEffect(() => {
        setDrawerOpen(false)
    }, [location.pathname])

    // Scroll to top on bus event
    const containerRef = useRef(null)

    useListener('scrollTopContainer', () => {
        if (containerRef.current) {
            containerRef.current.scrollTop = 0
        }
    })

    // Auth check
    const hasHeader = role === 'ADMIN' || role === 'MNP'

    // Scan detection
    const warrantyStartsWith = [
        'httpsbaltic§watchescomenw',
        'httpsbaltic§watchescomw',
        'httpsbaltic-watchescomw',
        'httpsbaltic-watchescomenw',
        'https://baltic-watches.com/w/',
        'baltic-watches.com/w/',
    ]

    const onScanHandler = (scannedString) => {
        if (scannedString.startsWith('BSKU:')) {
            const componentId = scannedString.replace('BSKU:', '')
            navigate(`/components/${componentId}`)
            return true
        }

        if (scannedString.startsWith('BRSC:')) {
            const serviceCaseId = scannedString.replace('BRSC:', '')
            navigate(`/service-cases/${serviceCaseId}`)
            return true
        }

        const isWarranty = warrantyStartsWith.some((prefix) =>
            scannedString.startsWith(prefix)
        )

        if (isWarranty) {
            const warrantyCode = warrantyStartsWith.reduce(
                (acc, prefix) => acc.replace(prefix, ''),
                scannedString
            )

            bus.emit('scan', {
                type: 'warranty',
                code: warrantyCode,
            })

            return true
        }

        return false
    }

    const onScanHandlerRef = useRef(onScanHandler)
    onScanHandlerRef.current = onScanHandler

    useNfcScanner(onScanHandler)

    useEffect(() => {
        const handlePaste = (event) => {
            const data = event.clipboardData.getData('text')
            onScanHandlerRef.current(data)
        }

        onScan.attachTo(document, {
            onScan: (scannedString) => onScanHandlerRef.current(scannedString),
        })

        window.addEventListener('paste', handlePaste)

        return () => {
            onScan.detachFrom(document)
            window.removeEventListener('paste', handlePaste)
        }
    }, [])

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader type="bars" size="sm" />
            </div>
        )
    }

    // Pas de rôle exploitable : soit le profil n'a pas pu être chargé (API
    // injoignable, session refusée), soit le compte n'a pas accès. On distingue
    // les deux au lieu de déduire un rôle du silence de l'API. Une erreur
    // survenue alors qu'un rôle est déjà connu (revalidation ratée) ne coupe
    // rien : l'interface reste affichée.
    if (!hasHeader) {
        if (error) {
            return (
                <GateScreen
                    title={t('error_server_title')}
                    message={t('error_server_message')}
                    detail={
                        error?.response?.status
                            ? `HTTP ${error.response.status}`
                            : error?.message
                    }
                    action={
                        <Button onClick={() => window.location.reload()}>
                            {t('retry')}
                        </Button>
                    }
                />
            )
        }

        return (
            <GateScreen
                title={t('error_no_access_title')}
                message={t('error_no_access_message')}
                action={
                    <Button
                        variant="light"
                        color="dark"
                        onClick={() => supabase.auth.signOut()}
                    >
                        {t('sign_out')}
                    </Button>
                }
            />
        )
    }

    return (
        <>
            <div className="h-screen flex overflow-hidden">
                {/* Desktop sidebar */}
                {!isMobile && (
                    <aside className="shrink-0 w-[280px] bg-gray-50 border-r border-gray-200/60 flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between h-[59px] shrink-0 border-b border-gray-200/60 px-4">
                            <Link to="/">
                                <BalticLogo className="w-20 text-gray-900" />
                            </Link>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <BackofficeLayoutNavigation />
                        </div>
                        <div className="border-t border-gray-200/60 px-2 py-2 flex flex-col gap-0.5">
                            <SidebarLocationButton />

                            <div className="flex items-center h-[38px] gap-1">
                                <Link
                                    to="/me"
                                    className="flex-1 min-w-0 flex items-center gap-3 px-4 rounded-xl py-2.5 h-full hover:bg-gray-100 transition-colors cursor-pointer no-underline"
                                >
                                    <User01
                                        width={18}
                                        height={18}
                                        className="shrink-0 text-gray-400"
                                    />
                                    <span className="text-sm text-gray-600 truncate">
                                        {userName}
                                    </span>
                                </Link>

                                <SidebarNotificationsButton />
                            </div>
                        </div>
                    </aside>
                )}

                {/* Main content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {isMobile && (
                        <div className="relative shrink-0 z-40 h-[52px] px-3 bg-gray-50 border-b border-gray-200/60">
                            {/* Logo centré en absolute */}
                            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                                <Link to="/" className="pointer-events-auto">
                                    <BalticLogo className="w-20 text-gray-900" />
                                </Link>
                            </div>
                            {/* Gauche: nav + terrain, Droite: recherche + compte */}
                            <div className="relative flex items-center justify-between h-full">
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setDrawerOpen(true)}
                                        className="p-2 -ml-1 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                    >
                                        <Menu01 width={20} height={20} />
                                    </button>
                                    <Tooltip label="Emplacement">
                                        <button
                                            onClick={() =>
                                                bus.emit('openCurrentLocation')
                                            }
                                            className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <MarkerPin01
                                                width={20}
                                                height={20}
                                            />
                                        </button>
                                    </Tooltip>
                                    <Tooltip label="Scanner">
                                        <button
                                            onClick={() =>
                                                bus.emit('openCameraScanner')
                                            }
                                            className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <Scan width={20} height={20} />
                                        </button>
                                    </Tooltip>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Tooltip label="Rechercher">
                                        <button
                                            onClick={() =>
                                                bus.emit('openGlobalSearch')
                                            }
                                            className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <SearchLg width={20} height={20} />
                                        </button>
                                    </Tooltip>
                                    <Tooltip label="Rechercher par numéro">
                                        <button
                                            onClick={() =>
                                                bus.emit('openNumberLookup')
                                            }
                                            className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <Hash02 width={20} height={20} />
                                        </button>
                                    </Tooltip>
                                    <SidebarNotificationsButton />
                                    <Tooltip label="Profil">
                                        <Link
                                            to="/me"
                                            className="p-2 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer"
                                        >
                                            <User01 width={20} height={20} />
                                        </Link>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    )}
                    <main
                        className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50"
                        style={{ overscrollBehaviorY: 'contain' }}
                        ref={containerRef}
                    >
                        {children}
                    </main>
                </div>
            </div>

            {/* Mobile bottom-sheet navigation */}
            {isMobile && (
                <Drawer.Root
                    open={drawerOpen}
                    onOpenChange={(open) => {
                        if (!open) setDrawerOpen(false)
                    }}
                >
                    <Drawer.Portal>
                        <Drawer.Overlay className="fixed inset-0 bg-black/30 z-50" />
                        <Drawer.Content className="bg-white flex flex-col rounded-t-2xl fixed bottom-0 left-0 right-0 h-[60vh] z-50 outline-hidden">
                            <div className="mx-auto w-10 h-1 shrink-0 rounded-full bg-gray-300 mt-3 mb-1" />
                            <div className="overflow-y-auto flex-1">
                                <BackofficeLayoutNavigation
                                    onNavigate={() => setDrawerOpen(false)}
                                />
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            )}

            <div className="hidden">
                <CurrentLocation />
                <CameraScannerButton onScan={onScanHandler} />
            </div>

            <TypesenseGlobalSearch />
            <NumberLookupSearch hideTrigger />
            <ScannedModals />
        </>
    )
}
